import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { describeProgram, type ProgramTrack, type ProgramView } from '../../programs/types';
import { moveLabel } from '../../programs/track';
import { REWARD_ICONS, type RewardIcon } from '../../settings/SettingsContext';
import { AddProgramsMenu, RewardMarks } from './shared';
import type { HomeModel } from './useHome';
import { useT } from '../../i18n/i18n';

function ProgramList({
  h,
  title,
  programs,
  emptyHint,
  track,
}: {
  h: HomeModel;
  title: string;
  programs: ProgramView[];
  emptyHint: string;
  /** Which list this is, so a drop onto the list a program is already on does nothing. */
  track: ProgramTrack;
}) {
  const t = useT();
  const [over, setOver] = useState(false);
  const { dragging, rewards, learnerId } = h;
  /* A DROP ONTO THE LIST IT IS ALREADY ON IS NOT A MOVE. Lighting up regardless would promise
     something pressing the mouse up could not deliver. */
  const accepts = dragging !== null && dragging.track !== track;

  return (
    <section
      className={`card${over && accepts ? ' drop-target' : ''}`}
      aria-labelledby={`${title.replace(/\s+/g, '-')}-h`}
      onDragOver={(e) => { if (accepts) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (accepts && dragging) void h.move(dragging);
        h.setDragging(null);
      }}
    >
      <h2 id={`${title.replace(/\s+/g, '-')}-h`}>{title}</h2>
      {programs.length === 0 ? (
        <p className="muted">{emptyHint}</p>
      ) : (
        <ul className="list program-rows">
          {programs.map((p) => (
            <li
              className={`list-item${dragging?.id === p.id ? ' is-dragging' : ''}`}
              key={p.id}
              draggable
              onDragStart={() => h.setDragging(p)}
              onDragEnd={() => h.setDragging(null)}
            >
              <div className="list-item-text">
                <div className="name">
                  {p.name}
                  {p.kind === 'studio' && <span className="pill">{t('Studio')}</span>}
                  {h.dueIds.has(p.id) && <span className="pill is-due">{t('Ready for a check')}</span>}
                  {rewards?.enabled && (
                    <>
                      {' '}
                      <RewardMarks
                        total={rewards.byProgram.find((r) => r.programId === p.id)?.total ?? 0}
                        icon={rewards.icon}
                        name={rewards.name}
                      />
                    </>
                  )}
                </div>
                <div className="list-item-meta">
                  {describeProgram(p)}
                </div>
              </div>
              <div className="toolbar">
                {/* A LINK IS DRAGGABLE BY ITSELF. Without this, starting a drag on one of these
                    carries the address rather than the row, and the browser shows the link as
                    what is being moved. The row is the thing that moves. */}
                <Link
                  className="btn"
                  draggable={false}
                  to={`/play/${p.id}${learnerId ? `?learner=${encodeURIComponent(learnerId)}` : ''}`}
                >
                  {t('Play')}
                </Link>
                {rewards?.enabled && (
                  <button
                    className="btn secondary reward-give"
                    onClick={() => void h.approve(p)}
                    disabled={h.approving === p.id}
                    aria-label={t('Give {reward} for {program}', { reward: rewards.name, program: p.name })}
                  >
                    {h.approving === p.id ? '…' : <><span aria-hidden="true">+</span><span aria-hidden="true">{REWARD_ICONS[rewards.icon as RewardIcon] ?? REWARD_ICONS.star}</span></>}
                  </button>
                )}
                {/* EVERYTHING ELSE IS BEHIND ONE CONTROL. Each feature switched on used to add
                    another button to this row until it filled the width and overflowed. Playing
                    is what the row is for, and giving a star happens during a sitting; the rest
                    are occasional, and they no longer make the row grow. */}
                <RowMenu label={t('More for {program}', { program: p.name })} text="⋯">
                  <ProgramMenuItems h={h} p={p} />
                </RowMenu>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ProgramMenuItems({ h, p }: { h: HomeModel; p: ProgramView }) {
  const t = useT();
  return (
    <>
      {h.settings.features.progressGraph && (
        <Link className="usermenu-item" role="menuitem" draggable={false} to={`/progress/${p.id}`}>
          {t('Progress')}
        </Link>
      )}
      <button type="button" className="usermenu-item" role="menuitem" onClick={() => void h.move(p)}>
        {moveLabel(p.track)}
      </button>
      <Link className="usermenu-item" role="menuitem" draggable={false} to={`/programs/${p.id}/edit`}>
        {t('Edit')}
      </Link>
      <button type="button" className="usermenu-item" role="menuitem" onClick={() => void h.copy(p)}>
        {t('Copy')}
      </button>
      {h.showPackages && (
        <button type="button" className="usermenu-item" role="menuitem" onClick={() => h.openAddToPackage(p)}>
          {t('Add to a set')}
        </button>
      )}
      {/* Out of the way, but not disguised as the others. */}
      <button type="button" className="usermenu-item danger" role="menuitem" onClick={() => h.requestDelete(p.id)}>
        {t('Delete')}
      </button>
    </>
  );
}

export default function ClassicHome({ h }: { h: HomeModel }) {
  const t = useT();
  const { settings, rewards, learners, learnerId } = h;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--home-bar-h', `${Math.round(bar.getBoundingClientRect().height)}px`));
    ro.observe(bar);
    return () => { ro.disconnect(); root.style.removeProperty('--home-bar-h'); };
  }, []);
  const filtered = (h.categoryFilter ? 1 : 0) + (h.kindFilter ? 1 : 0);
  const hasOptions = h.categories.length > 0 || h.kinds.length > 1;
  return (
    <div>
      <div className="home-bar" ref={barRef}>
        {hasOptions && (
          <button type="button" className="btn secondary" aria-expanded={optionsOpen} aria-controls="home-bar-options" onClick={() => setOptionsOpen((o) => !o)}>
            {filtered > 0 ? t('Filter ({n})', { n: filtered }) : t('Filter')} <span aria-hidden="true">{optionsOpen ? '▴' : '▾'}</span>
          </button>
        )}
        {optionsOpen && (
          <div id="home-bar-options" className="home-bar-options">
            {h.categories.length > 0 && (
              <div className="field">
                <select aria-label={t('Category')} value={h.categoryFilter} onChange={(e) => h.setCategoryFilter(e.target.value)}>
                  <option value="">{t('All categories')}</option>
                  {h.categories.map((c) => <option key={c} value={c}>{t(c)}</option>)}
                </select>
              </div>
            )}
            {h.kinds.length > 1 && (
              <div className="field">
                <select aria-label={t('Type of game')} value={h.kindFilter} onChange={(e) => h.setKindFilter(e.target.value)}>
                  <option value="">{t('All game types')}</option>
                  {h.kinds.map((k) => <option key={k.id} value={k.id}>{t(k.label)}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
        <div className="home-bar-end">
          <AddProgramsMenu h={h} />
        </div>
      </div>

      <div className="home-top">
        <div className="toolbar home-controls">
          <AddProgramsMenu h={h} />
          {h.categories.length > 0 && (
            <div className="field" style={{ margin: 0, maxWidth: 360 }}>
              <select
                id="program-category"
                aria-label={t('Category')}
                value={h.categoryFilter}
                onChange={(e) => h.setCategoryFilter(e.target.value)}
              >
                <option value="">{t('All categories')}</option>
                {h.categories.map((c) => <option key={c} value={c}>{t(c)}</option>)}
              </select>
            </div>
          )}
          {h.kinds.length > 1 && (
            <div className="field" style={{ margin: 0, maxWidth: 360 }}>
              <select
                id="program-kind"
                aria-label={t('Type of game')}
                value={h.kindFilter}
                onChange={(e) => h.setKindFilter(e.target.value)}
              >
                <option value="">{t('All game types')}</option>
                {h.kinds.map((k) => <option key={k.id} value={k.id}>{t(k.label)}</option>)}
              </select>
            </div>
          )}
        </div>
        {/* Only when rewards are actually being given. A large "0 stars" on an account that does
            not use them is noise dressed up as a figure. */}
        {settings.features.rewardTotal && rewards?.enabled && (
          <div className="reward-total home-reward" role="region" aria-label={t('Rewards collected')}>
            {rewards.spending ? (
              <Link className="reward-total-link" to="/rewards">
                <span className="reward-total-glyph" aria-hidden="true">
                  {REWARD_ICONS[rewards.icon as RewardIcon] ?? REWARD_ICONS.star}
                </span>
                <strong className="figure">{rewards.total}</strong>
                <span className="muted">{t('{reward} to spend', { reward: rewards.name })}</span>
              </Link>
            ) : (
              <>
                <span className="reward-total-glyph" aria-hidden="true">
                  {REWARD_ICONS[rewards.icon as RewardIcon] ?? REWARD_ICONS.star}
                </span>
                <strong className="figure">{rewards.total}</strong>
                <span className="muted">
                  {learners.length > 1 && learnerId
                    ? t('{reward} collected by {learner}', { reward: rewards.name, learner: learners.find((l) => l.id === learnerId)?.name ?? t('this learner') })
                    : t('{reward} collected', { reward: rewards.name })}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {h.error && <div className="error" role="alert">{h.error}</div>}

      {h.loading ? (
        <p className="muted">{t('Loading…')}</p>
      ) : (
        <>
          <ProgramList
            h={h}
            title={t('Current programs')}
            programs={h.current}
            emptyHint={t('Programs the learner is working on appear here.')}
            track="CURRENT"
          />
          <ProgramList
            h={h}
            title={t('Maintenance programs')}
            programs={h.maintenance}
            emptyHint={t('Mastered programs you want to revisit periodically appear here.')}
            track="MAINTENANCE"
          />
        </>
      )}
    </div>
  );
}
